import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import {
  SurveyAnswerValue,
  SurveyLocalizedText,
  SurveyQuestion,
  SurveyScale,
} from '../../models/survey.models';
import { SurveyStarRatingComponent } from '../survey-star-rating/survey-star-rating.component';

@Component({
  selector: 'app-survey-question',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    SurveyStarRatingComponent,
  ],
  templateUrl: './survey-question.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './survey-question.component.scss',
})
export class SurveyQuestionComponent {
  @Input({ required: true }) question!: SurveyQuestion;
  @Input({ required: true }) fieldId!: string;
  @Input({ required: true }) control!: FormControl<SurveyAnswerValue>;
  @Input() locale: 'fi' | 'en' = 'fi';
  @Input() requiredIndicator = 'Required';
  @Input() textareaPlaceholder = '';

  protected text(value?: SurveyLocalizedText | string): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (this.locale === 'en' && value.en) {
      return value.en;
    }

    return value.fi || value.en || '';
  }

  protected getScaleValues(): number[] {
    const scale = this.question.scale ?? this.getDefaultScale(this.question.type);
    if (!scale) {
      return [];
    }

    const values: number[] = [];
    for (let value = scale.min; value <= scale.max; value += scale.step) {
      values.push(value);
    }

    return values;
  }

  protected onAnswerChange(value: SurveyAnswerValue): void {
    const normalizedValue = this.normalizeAnswerValue(this.question.type, value);
    if (normalizedValue !== value) {
      this.control.setValue(normalizedValue, { emitEvent: false });
    }
  }

  protected onStarRatingChange(rating: number): void {
    const normalizedRating = this.normalizeAnswerValue('star', rating);
    this.control.setValue(normalizedRating);
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  protected getStarMax(): number {
    const scale = this.question.scale ?? this.getDefaultScale('star');
    return scale?.max ?? 5;
  }

  protected getStarControlValue(): number {
    const normalizedValue = this.normalizeAnswerValue('star', this.control.value);
    if (typeof normalizedValue !== 'number' || Number.isNaN(normalizedValue)) {
      return 0;
    }

    const scale = this.question.scale ?? this.getDefaultScale('star');
    if (!scale) {
      return normalizedValue;
    }

    return Math.min(scale.max, Math.max(scale.min, normalizedValue));
  }

  private getDefaultScale(questionType: SurveyQuestion['type']): SurveyScale | null {
    if (questionType === 'nps') {
      return { min: 1, max: 10, step: 1 };
    }

    if (questionType === 'star') {
      return { min: 1, max: 5, step: 1 };
    }

    return null;
  }

  private normalizeAnswerValue(
    questionType: SurveyQuestion['type'],
    value: SurveyAnswerValue,
  ): SurveyAnswerValue {
    if ((questionType === 'nps' || questionType === 'star') && typeof value === 'string') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) {
        return numericValue;
      }
    }

    return value;
  }
}
